import { Activity, Globe, type LucideIcon, PackageCheck, Rocket, Target, Users } from "lucide-react";
import { COUNTRY, COUNTRY_ADJ, VISION_YEAR } from "../../lib/tenant";

/**
 * Workspace registry — the five top-level destinations of the dashboard.
 *
 * Plan 17 retired the dark vertical peer-rail; the workspace switch now lives
 * in a dropdown at the top of the left sidebar (see `workspace-switcher.tsx`),
 * and the app opens on a first-run landing hub (`workspace-hub.tsx`) with one
 * card per workspace. This module is the single source of truth both consume —
 * it replaces the old `PEERS` / `PeerId` exports that lived in `peer-rail.tsx`.
 *
 *   Intelligence · Demand Signals · Segmentation · Prioritization ·
 *   Product Matching · Go to Market
 *
 * Intelligence and Demand Signals are scoped by geography (Global/Saudi); that
 * toggle stays in the sidebar, below the switcher.
 */

export type WorkspaceId = "intelligence" | "demand-signals" | "segmentation" | "prioritization" | "product" | "gtm";

export interface Workspace {
  id: WorkspaceId;
  /** Hub card number ("01"–"06"). */
  n: string;
  label: string;
  icon: LucideIcon;
  /** Carries the Global/Saudi scope switcher (Intelligence, Demand Signals). */
  scoped: boolean;
  /** `live` workspaces open; `soon` ones render a quiet "Coming soon" on the hub. */
  status: "live" | "soon";
  /** One-line hub-card copy. */
  blurb: string;
  /**
   * Workspace accent hue (design-system §0.3) — a fixed brand hex applied to the
   * italic accent word in every section opening within this workspace, and to
   * the workspace's hub card. Deliberately literal (not a theme var) so the
   * accent word stays the same signature colour in both light and dark.
   */
  accent: string;
}

const ALL_WORKSPACES: Workspace[] = [
  {
    id: "intelligence",
    n: "01",
    label: "Intelligence",
    icon: Globe,
    scoped: true,
    status: "live",
    blurb: `A live view of global and ${COUNTRY_ADJ} tourism - demand, drivers and the forward outlook.`,
    accent: "#82BAFF",
  },
  {
    id: "demand-signals",
    n: "02",
    label: "Demand Signals",
    icon: Activity,
    scoped: true,
    status: "live",
    blurb: `Live demand signals - behaviour, search, sentiment and booking intent, across global and ${COUNTRY_ADJ} markets.`,
    accent: "#FF8A5C",
  },
  {
    id: "segmentation",
    n: "03",
    label: "Segmentation",
    icon: Users,
    scoped: false,
    status: "live",
    blurb: "Genuine-traveler cohorts resolved from millions of social signals, scored into demand archetypes.",
    accent: "#5DD4A4",
  },
  {
    id: "prioritization",
    n: "04",
    label: "Prioritization",
    icon: Target,
    scoped: false,
    status: "live",
    blurb: `Where ${COUNTRY} should play - every source market ranked by opportunity and momentum.`,
    accent: "#A89BFF",
  },
  {
    id: "product",
    n: "05",
    label: "Product Matching",
    icon: PackageCheck,
    scoped: false,
    status: "live",
    blurb: `What to sell and what to build - ${COUNTRY}'s bookable supply benchmarked, and the gap to the ${VISION_YEAR} targets.`,
    accent: "#FF6B9D",
  },
  {
    id: "gtm",
    n: "06",
    label: "Go to Market",
    icon: Rocket,
    scoped: false,
    status: "live",
    blurb:
      "The full go-to-market plan - market performance, where to play, how to win and what it takes, per source market.",
    accent: "#FFC02E",
  },
];

/**
 * Segmentation is available on BOTH deployments, and the way that was made safe
 * is worth stating because the obvious way is not safe.
 *
 * Its three sections read the Track A tables — the scraped social corpus: real
 * Reddit and Twitter posts by real people. Until 2026-08-07 this workspace was
 * withheld from every non-client deployment, because the two ways to open it
 * that suggest themselves are both wrong. Serving the SAME rows with the labels
 * changed republishes real people's posts under a country they never wrote
 * about. Serving them unlabelled still shows the client's corpus to a second
 * audience.
 *
 * What was actually done: the Track A tables were scoped on their (pre-existing,
 * previously unused) `tenant_id` column, and the demo was given a corpus of its
 * own — fabricated people, fabricated posts, generated by
 * `seed/wonderland/segmentation.ts` and read through the same repos and the same
 * payload builders as the client's. Neither deployment can see the other's
 * cohorts: the scope is a WHERE clause in the repo, not a check the route has to
 * remember (see the server's `requireCohort`).
 *
 * So there is no filter here any more. If a THIRD tenant is ever added, it needs
 * a seeded corpus before it needs this list — a tenant with no cohorts renders
 * an empty Explorer, not the client's.
 */
export const WORKSPACES: Workspace[] = ALL_WORKSPACES;

/** True when this deployment shows the workspace at all. Callers that hold a
 *  hardcoded workspace id — the landing hub's cards, a restored deep link —
 *  must check, or they offer a door that opens onto nothing. */
export const workspaceAvailable = (id: WorkspaceId): boolean => WORKSPACES.some((w) => w.id === id);

// The three lookups below are built from the FULL registry, not the filtered
// navigation list: a withheld workspace still has a label and an accent, and a
// lookup that returned undefined would crash a caller that merely mentions it.
export const WORKSPACE_LABEL: Record<WorkspaceId, string> = Object.fromEntries(
  ALL_WORKSPACES.map((w) => [w.id, w.label]),
) as Record<WorkspaceId, string>;

export const WORKSPACE_BY_ID: Record<WorkspaceId, Workspace> = Object.fromEntries(
  ALL_WORKSPACES.map((w) => [w.id, w]),
) as Record<WorkspaceId, Workspace>;

/** Per-workspace accent hue (design-system §0.3) — the single source of truth for
 *  the italic accent-word colour used across a workspace's section openings. */
export const WORKSPACE_ACCENT: Record<WorkspaceId, string> = Object.fromEntries(
  ALL_WORKSPACES.map((w) => [w.id, w.accent]),
) as Record<WorkspaceId, string>;
